import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { HOME_DEFAULTS } from "@/lib/publicContent";
import { ImageUploader } from "@/components/cms/ImageUploader";

type FieldType = "text" | "textarea" | "image" | "list" | "steps" | "toggle" | "cards";
interface Field {
  name: string;
  label: string;
  type: FieldType;
}
interface SectionDef {
  key: string;
  label: string;
  description: string;
  fields: Field[];
}

const SECTIONS: SectionDef[] = [
  {
    key: "hero",
    label: "Hero",
    description: "Sezione principale in cima alla homepage.",
    fields: [
      { name: "eyebrow", label: "Sopra-titolo", type: "text" },
      { name: "title_line1", label: "Titolo — riga 1", type: "text" },
      { name: "title_line2", label: "Titolo — riga 2 (evidenziata)", type: "text" },
      { name: "paragraph", label: "Paragrafo", type: "textarea" },
      { name: "cta_primary", label: "Bottone principale", type: "text" },
      { name: "cta_secondary", label: "Bottone secondario", type: "text" },
      { name: "badge", label: "Etichetta immagine", type: "text" },
      { name: "image_url", label: "Immagine hero", type: "image" },
    ],
  },
  {
    key: "ticker",
    label: "Ticker scorrevole",
    description: "Le parole che scorrono nella barra sotto l'hero.",
    fields: [{ name: "items", label: "Voci", type: "list" }],
  },
  {
    key: "how",
    label: "Come funziona",
    description: "Intestazione e le 3 schede del processo (le icone restano fisse).",
    fields: [
      { name: "eyebrow", label: "Sopra-titolo", type: "text" },
      { name: "title_line1", label: "Titolo — riga 1", type: "text" },
      { name: "title_line2", label: "Titolo — riga 2", type: "text" },
      { name: "description", label: "Descrizione", type: "textarea" },
      { name: "steps", label: "Schede (max 3 mostrate)", type: "steps" },
    ],
  },
  {
    key: "portfolio",
    label: "Sezione Portfolio",
    description: "Testi dell'anteprima portfolio (i progetti arrivano dalla sezione Portfolio).",
    fields: [
      { name: "eyebrow", label: "Sopra-titolo", type: "text" },
      { name: "title", label: "Titolo", type: "text" },
      { name: "link_label", label: "Link 'Tutti'", type: "text" },
      { name: "empty_title", label: "Titolo (nessun progetto)", type: "text" },
      { name: "empty_text", label: "Testo (nessun progetto)", type: "text" },
      { name: "empty_cta", label: "Bottone (nessun progetto)", type: "text" },
    ],
  },
  {
    key: "reviews",
    label: "Sezione Recensioni",
    description: "Titolo del carosello recensioni (le recensioni arrivano dalla sezione dedicata).",
    fields: [{ name: "title", label: "Titolo", type: "text" }],
  },
  {
    key: "faq",
    label: "Sezione FAQ",
    description: "Testi dell'anteprima FAQ (le domande arrivano dalla sezione FAQ).",
    fields: [
      { name: "eyebrow", label: "Sopra-titolo", type: "text" },
      { name: "title", label: "Titolo", type: "text" },
      { name: "link_label", label: "Link 'Tutte le FAQ'", type: "text" },
    ],
  },
  {
    key: "cta",
    label: "Banda finale (CTA)",
    description: "La banda colorata di chiusura con l'invito al configuratore.",
    fields: [
      { name: "title_line1", label: "Titolo — riga 1", type: "text" },
      { name: "title_line2", label: "Titolo — riga 2", type: "text" },
      { name: "cta_label", label: "Bottone", type: "text" },
    ],
  },
  {
    key: "promo",
    label: "Blocco promo (Home)",
    description: "Blocco compatto in Home verso 'Scarpe da cerimonia' e 'Aziende / Bulk'. Disattivabile.",
    fields: [
      { name: "__published", label: "Mostra il blocco in Home", type: "toggle" },
      { name: "eyebrow", label: "Sopra-titolo", type: "text" },
      { name: "title", label: "Titolo", type: "text" },
      { name: "text", label: "Testo", type: "textarea" },
      { name: "cards", label: "Card", type: "cards" },
    ],
  },
];

const SECTION_ORDER: Record<string, number> = Object.fromEntries(SECTIONS.map((s, i) => [s.key, i]));

type Values = Record<string, any>;

export default function HomepageEditor() {
  const [rows, setRows] = useState<Record<string, any>>({});
  const [forms, setForms] = useState<Record<string, Values>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get<any[]>("/admin/homepage-sections");
        const byKey: Record<string, any> = {};
        (data || []).forEach((r) => (byKey[r.key] = r));
        const f: Record<string, Values> = {};
        SECTIONS.forEach((sec) => {
          const merged: Values = JSON.parse(JSON.stringify(HOME_DEFAULTS[sec.key] || {}));
          const existing = byKey[sec.key];
          if (existing) {
            Object.assign(merged, existing.data || {});
            if (existing.image_url) merged.image_url = existing.image_url;
          }
          merged.__published = existing ? existing.published !== false : true;
          f[sec.key] = merged;
        });
        setRows(byKey);
        setForms(f);
      } catch (err: any) {
        toast.error(formatApiError(err.response?.data?.detail) || err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const setField = (secKey: string, name: string, value: any) =>
    setForms((p) => ({ ...p, [secKey]: { ...p[secKey], [name]: value } }));

  const save = async (sec: SectionDef) => {
    setSaving(sec.key);
    try {
      const values = { ...forms[sec.key] };
      const enabled = values.__published;
      delete values.__published;
      const hasImage = sec.fields.some((fd) => fd.type === "image");
      const image_url = values.image_url ?? null;
      delete values.image_url;
      const payload: Values = {
        key: sec.key,
        data: values,
        published: sec.key === "promo" ? enabled !== false : true,
        sort_order: SECTION_ORDER[sec.key],
      };
      if (hasImage) payload.image_url = image_url;

      const existing = rows[sec.key];
      const res = existing
        ? await api.put(`/admin/homepage-sections/${existing.id}`, payload)
        : await api.post("/admin/homepage-sections", payload);
      setRows((p) => ({ ...p, [sec.key]: res.data }));
      toast.success(`Sezione "${sec.label}" salvata`);
    } catch (err: any) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(null);
    }
  };

  if (loading)
    return (
      <div className="py-24 flex justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );

  return (
    <div data-testid="homepage-editor">
      <div className="mb-10">
        <p className="text-xs uppercase tracking-[0.2em] font-bold text-secondary mb-2">CMS</p>
        <h1 className="font-display font-black tracking-tighter text-4xl">Homepage</h1>
        <p className="text-muted-foreground text-sm mt-1 max-w-xl">
          Modifica i testi e le immagini della homepage. Il layout resta invariato. Salva ogni sezione singolarmente.
        </p>
      </div>

      <div className="space-y-6">
        {SECTIONS.map((sec) => (
          <motion.div
            key={sec.key}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="border border-white/10 bg-card p-6 md:p-8"
            data-testid={`home-section-${sec.key}`}
          >
            <div className="flex items-start justify-between gap-4 mb-6">
              <div>
                <h2 className="font-display font-bold text-xl">{sec.label}</h2>
                <p className="text-xs text-muted-foreground mt-1">{sec.description}</p>
              </div>
              <button
                data-testid={`home-save-${sec.key}`}
                onClick={() => save(sec)}
                disabled={saving === sec.key}
                className="shrink-0 flex items-center gap-2 bg-primary text-primary-foreground font-bold uppercase tracking-[0.12em] text-xs px-5 py-3 hover:-translate-y-[2px] transition-transform disabled:opacity-60"
              >
                {saving === sec.key ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva"}
              </button>
            </div>

            <div className="grid gap-5 md:grid-cols-2">
              {sec.fields.map((fd) => (
                <div key={fd.name} className={["textarea", "list", "steps", "cards", "toggle"].includes(fd.type) ? "md:col-span-2" : ""}>
                  <FieldControl
                    secKey={sec.key}
                    field={fd}
                    value={forms[sec.key]?.[fd.name]}
                    onChange={(v) => setField(sec.key, fd.name, v)}
                  />
                </div>
              ))}
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

const Label: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <label className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground block mb-2">{children}</label>
);

const inputCls = "w-full border border-input bg-black/50 py-2.5 px-3 text-sm outline-none focus:border-primary transition-colors";

const FieldControl: React.FC<{
  secKey: string;
  field: Field;
  value: any;
  onChange: (v: any) => void;
}> = ({ secKey, field, value, onChange }) => {
  const testid = `home-field-${secKey}-${field.name}`;

  if (field.type === "image")
    return (
      <div>
        <Label>{field.label}</Label>
        <ImageUploader value={value} onChange={onChange} testid={testid} />
      </div>
    );

  if (field.type === "textarea")
    return (
      <div>
        <Label>{field.label}</Label>
        <textarea data-testid={testid} value={value ?? ""} onChange={(e) => onChange(e.target.value)} rows={3} className={inputCls} />
      </div>
    );

  if (field.type === "list") {
    const items: string[] = Array.isArray(value) ? value : [];
    return (
      <div data-testid={testid}>
        <Label>{field.label}</Label>
        <div className="space-y-2">
          {items.map((it, i) => (
            <div key={i} className="flex gap-2">
              <input
                data-testid={`${testid}-item-${i}`}
                value={it}
                onChange={(e) => onChange(items.map((x, xi) => (xi === i ? e.target.value : x)))}
                className={inputCls}
              />
              <button
                type="button"
                data-testid={`${testid}-remove-${i}`}
                onClick={() => onChange(items.filter((_, xi) => xi !== i))}
                className="shrink-0 px-3 border border-white/15 text-muted-foreground hover:text-primary transition-colors"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
          <button
            type="button"
            data-testid={`${testid}-add`}
            onClick={() => onChange([...items, ""])}
            className="flex items-center gap-2 text-xs uppercase tracking-[0.12em] font-bold text-muted-foreground hover:text-secondary transition-colors"
          >
            <Plus className="h-4 w-4" /> Aggiungi voce
          </button>
        </div>
      </div>
    );
  }

  if (field.type === "steps") {
    const steps: { title: string; text: string }[] = Array.isArray(value) ? value : [];
    const update = (i: number, patch: Partial<{ title: string; text: string }>) =>
      onChange(steps.map((s, xi) => (xi === i ? { ...s, ...patch } : s)));
    return (
      <div data-testid={testid}>
        <Label>{field.label}</Label>
        <div className="space-y-4">
          {steps.map((s, i) => (
            <div key={i} className="border border-white/10 p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs uppercase tracking-wider font-bold text-secondary">Scheda {i + 1}</span>
                <button
                  type="button"
                  data-testid={`${testid}-remove-${i}`}
                  onClick={() => onChange(steps.filter((_, xi) => xi !== i))}
                  className="text-muted-foreground hover:text-primary transition-colors"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
              <input
                data-testid={`${testid}-title-${i}`}
                value={s.title ?? ""}
                placeholder="Titolo"
                onChange={(e) => update(i, { title: e.target.value })}
                className={inputCls}
              />
              <textarea
                data-testid={`${testid}-text-${i}`}
                value={s.text ?? ""}
                placeholder="Testo"
                onChange={(e) => update(i, { text: e.target.value })}
                rows={2}
                className={inputCls}
              />
            </div>
          ))}
          <button
            type="button"
            data-testid={`${testid}-add`}
            onClick={() => onChange([...steps, { title: "", text: "" }])}
            className="flex items-center gap-2 text-xs uppercase tracking-[0.12em] font-bold text-muted-foreground hover:text-secondary transition-colors"
          >
            <Plus className="h-4 w-4" /> Aggiungi scheda
          </button>
        </div>
      </div>
    );
  }

  if (field.type === "toggle") {
    return (
      <label className="flex items-center gap-3 cursor-pointer select-none">
        <input type="checkbox" data-testid={testid} checked={value !== false} onChange={(e) => onChange(e.target.checked)} className="h-4 w-4 accent-primary" />
        <span className="text-sm">{field.label}</span>
      </label>
    );
  }

  if (field.type === "cards") {
    const cards: any[] = Array.isArray(value) ? value : [];
    const upd = (i: number, patch: any) => onChange(cards.map((c, xi) => (xi === i ? { ...c, ...patch } : c)));
    return (
      <div data-testid={testid}>
        <Label>{field.label}</Label>
        <div className="space-y-4">
          {cards.map((c, i) => (
            <div key={i} className="border border-white/10 p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs uppercase tracking-wider font-bold text-secondary">Card {i + 1}</span>
                <button type="button" data-testid={`${testid}-remove-${i}`} onClick={() => onChange(cards.filter((_, xi) => xi !== i))} className="text-muted-foreground hover:text-primary transition-colors">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
              <input data-testid={`${testid}-title-${i}`} value={c.title ?? ""} placeholder="Titolo" onChange={(e) => upd(i, { title: e.target.value })} className={inputCls} />
              <textarea data-testid={`${testid}-text-${i}`} value={c.text ?? ""} placeholder="Testo" onChange={(e) => upd(i, { text: e.target.value })} rows={2} className={inputCls} />
              <div className="grid grid-cols-2 gap-2">
                <input data-testid={`${testid}-btntext-${i}`} value={c.button_text ?? ""} placeholder="Testo pulsante" onChange={(e) => upd(i, { button_text: e.target.value })} className={inputCls} />
                <input data-testid={`${testid}-btnlink-${i}`} value={c.button_link ?? ""} placeholder="Link pulsante" onChange={(e) => upd(i, { button_link: e.target.value })} className={inputCls} />
              </div>
              <div>
                <span className="text-[11px] uppercase tracking-wider text-muted-foreground block mb-1">Immagine (opzionale)</span>
                <ImageUploader value={c.image_url || null} onChange={(u) => upd(i, { image_url: u || "" })} testid={`${testid}-img-${i}`} />
              </div>
            </div>
          ))}
          <button type="button" data-testid={`${testid}-add`} onClick={() => onChange([...cards, { title: "", text: "", button_text: "Scopri", button_link: "", image_url: "" }])} className="flex items-center gap-2 text-xs uppercase tracking-[0.12em] font-bold text-muted-foreground hover:text-secondary transition-colors">
            <Plus className="h-4 w-4" /> Aggiungi card
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Label>{field.label}</Label>
      <input data-testid={testid} type="text" value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={inputCls} />
    </div>
  );
};
